import { formatYear } from "../lib/format";
import { feedback } from "../services/feedback";

type TimelineOptions = {
  canvas: HTMLCanvasElement;
  label: HTMLElement;
  minYear: number;
  maxYear: number;
  startYear: number;
  onChange: (year: number) => void;
};

type TimelineScale = {
  span: number;
  major: number;
  minor: number;
};

// Below this rate, snap to integer years to avoid stutter. Set very low so
// fractional pixels-per-year (smooth, slow scrubbing) is allowed.
const FRACTIONAL_PIXELS_THRESHOLD = 0.02;
const NUDGE_KEYS = ["ArrowLeft", "ArrowRight"];

export class TimelineControl {
  private minYear: number;
  private maxYear: number;
  private year: number;
  private scaleIndex = 0;
  private dragging = false;
  private pointerId: number | null = null;
  private lastX = 0;
  private dragAccumulator = 0;
  private slowDrag = false;

  constructor(private readonly options: TimelineOptions) {
    this.minYear = options.minYear;
    this.maxYear = options.maxYear;
    this.year = options.startYear;
    this.bindEvents();
    this.render();
  }

  setRange(minYear: number, maxYear: number, startYear: number): void {
    this.minYear = minYear;
    this.maxYear = maxYear;
    this.year = this.clamp(startYear);
    // Pick a default zoom tuned to the era range. Narrow filters (modern,
    // medieval, etc.) start moderately zoomed in for fine-grained control.
    // Huge ranges (the "all" archive, ~37000 years) start more zoomed OUT so
    // the player can actually traverse millennia without endless scrolling —
    // the +/− buttons / wheel still let them dial in once they're close.
    const range = maxYear - minYear;
    const scales = this.currentScales();
    if (range > 30000) {
      // 7-step ladder for huge ranges: choose span ≈ 6000 (index 2).
      this.scaleIndex = Math.min(scales.length - 1, 2);
    } else {
      this.scaleIndex = Math.min(scales.length - 1, Math.max(0, scales.length - 3));
    }
    this.options.onChange(this.year);
    this.render();
  }

  getYear(): number {
    return Math.round(this.year);
  }

  setYear(year: number): void {
    this.year = this.clamp(Math.round(year));
    this.options.onChange(this.getYear());
    this.render();
  }

  zoomToFinest(): void {
    const scales = this.currentScales();
    this.scaleIndex = scales.length - 1;
    this.render();
  }

  zoomIn(): void {
    const scales = this.currentScales();
    this.scaleIndex = Math.min(scales.length - 1, this.scaleIndex + 1);
    this.render();
  }

  zoomOut(): void {
    this.scaleIndex = Math.max(0, this.scaleIndex - 1);
    this.render();
  }

  canZoomIn(): boolean {
    return this.scaleIndex < this.currentScales().length - 1;
  }

  canZoomOut(): boolean {
    return this.scaleIndex > 0;
  }

  private bindEvents(): void {
    this.options.canvas.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    window.addEventListener("pointermove", (event) => this.onPointerMove(event));
    window.addEventListener("pointerup", (event) => this.onPointerUp(event));
    window.addEventListener("pointercancel", (event) => this.onPointerUp(event));
    window.addEventListener("keydown", (event) => this.onKeyDown(event));
    window.addEventListener("keyup", (event) => this.onKeyUp(event));
    this.options.canvas.addEventListener("wheel", (event) => this.onWheel(event), { passive: false });
    window.addEventListener("resize", () => this.render());
  }

  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }

    this.dragging = true;
    this.pointerId = event.pointerId;
    this.lastX = event.clientX;
    this.dragAccumulator = 0;
    this.slowDrag = event.shiftKey;
    this.options.canvas.setPointerCapture?.(event.pointerId);
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.pointerId) {
      return;
    }

    this.slowDrag = event.shiftKey;

    const drawableWidth = Math.max(1, this.options.canvas.clientWidth - 56);
    const scale = this.currentScale();
    let yearsPerPixel = scale.span / drawableWidth;

    // Year-aware dampening: very fine in modern, faster in deep BCE.
    yearsPerPixel *= sensitivityForYear(this.year);

    if (this.slowDrag) {
      yearsPerPixel = yearsPerPixel * 0.18;
    }

    if (yearsPerPixel < FRACTIONAL_PIXELS_THRESHOLD) {
      // Below the threshold, treat as effectively zero so a pixel of jitter
      // doesn't commit a year. The accumulator handles fractional values
      // across multiple frames just fine.
      yearsPerPixel = Math.max(0, yearsPerPixel);
    }

    const deltaX = event.clientX - this.lastX;
    this.lastX = event.clientX;

    this.dragAccumulator -= deltaX * yearsPerPixel;

    const yearsToApply = Math.trunc(this.dragAccumulator);
    if (yearsToApply !== 0) {
      this.year = this.clamp(this.year + yearsToApply);
      this.dragAccumulator -= yearsToApply;
      this.options.onChange(this.getYear());
      this.render();
      feedback.yearTick(yearsToApply > 0 ? "up" : "down");
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }

    this.dragging = false;
    this.pointerId = null;
    this.dragAccumulator = 0;
    this.slowDrag = false;
    this.options.canvas.releasePointerCapture?.(event.pointerId);
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (!this.dragging && document.activeElement !== this.options.canvas) {
      // Only handle arrow keys when the canvas is focused or being dragged.
      if (!NUDGE_KEYS.includes(event.key)) return;
    }

    if (event.key === "ArrowLeft") {
      const step = event.shiftKey ? 1 : 5;
      this.year = this.clamp(this.year - step);
      this.options.onChange(this.getYear());
      this.render();
      event.preventDefault();
    } else if (event.key === "ArrowRight") {
      const step = event.shiftKey ? 1 : 5;
      this.year = this.clamp(this.year + step);
      this.options.onChange(this.getYear());
      this.render();
      event.preventDefault();
    }
  }

  private onKeyUp(_event: KeyboardEvent): void {
    // Reserved for future shift-modifier interactions.
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    const direction = Math.sign(event.deltaY);
    const scales = this.currentScales();
    this.scaleIndex = Math.max(0, Math.min(scales.length - 1, this.scaleIndex + direction));
    this.render();
  }

  private render(): void {
    const canvas = this.options.canvas;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(320, Math.round(rect.width || canvas.width));
    const height = Math.max(74, Math.round(rect.height || canvas.height));

    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, width, height);
    this.options.label.textContent = formatYear(this.getYear());

    const centerX = width / 2;
    const centerY = Math.round(height / 2);
    const pad = 24;
    const drawable = Math.max(1, width - pad * 2);
    const scale = this.currentScale();
    const start = this.year - scale.span / 2;
    const end = this.year + scale.span / 2;

    context.strokeStyle = "rgba(255,255,255,0.42)";
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(pad, centerY);
    context.lineTo(width - pad, centerY);
    context.stroke();

    const firstMinor = Math.ceil(start / scale.minor) * scale.minor;
    for (let tick = firstMinor; tick <= end; tick += scale.minor) {
      if (tick < this.minYear || tick > this.maxYear) {
        continue;
      }

      const x = centerX + ((tick - this.year) / scale.span) * drawable;
      const major = Math.abs(tick / scale.major - Math.round(tick / scale.major)) < 0.0001;
      context.strokeStyle = major ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.36)";
      context.lineWidth = major ? 1.5 : 1;
      context.beginPath();
      context.moveTo(x, centerY - (major ? 19 : 10));
      context.lineTo(x, centerY + (major ? 19 : 10));
      context.stroke();

      if (major) {
        context.save();
        context.translate(x, centerY + 31);
        context.fillStyle = "rgba(255,255,255,0.86)";
        context.font = "600 11px Inter, system-ui, sans-serif";
        context.textAlign = "center";
        context.fillText(formatTimelineTick(tick), 0, 0);
        context.restore();
      }
    }

    context.fillStyle = "#ffffff";
    context.beginPath();
    context.moveTo(centerX, centerY - 27);
    context.lineTo(centerX - 8, centerY - 38);
    context.lineTo(centerX + 8, centerY - 38);
    context.closePath();
    context.fill();
    context.beginPath();
    context.moveTo(centerX, centerY + 27);
    context.lineTo(centerX - 8, centerY + 38);
    context.lineTo(centerX + 8, centerY + 38);
    context.closePath();
    context.fill();
  }

  private currentScale(): TimelineScale {
    const scales = this.currentScales();
    return scales[Math.min(this.scaleIndex, scales.length - 1)];
  }

  private currentScales(): TimelineScale[] {
    const range = this.maxYear - this.minYear;

    if (range > 30000) {
      return [
        { span: 50000, major: 10000, minor: 2500 },
        { span: 24000, major: 5000, minor: 1000 },
        { span: 6000, major: 1000, minor: 250 },
        { span: 1200, major: 250, minor: 50 },
        { span: 240, major: 50, minor: 10 },
        { span: 60, major: 10, minor: 2 },
        { span: 24, major: 5, minor: 1 }
      ];
    }

    if (range > 10000) {
      return [
        { span: 24000, major: 5000, minor: 1000 },
        { span: 6000, major: 1000, minor: 250 },
        { span: 1200, major: 250, minor: 50 },
        { span: 240, major: 50, minor: 10 },
        { span: 60, major: 10, minor: 2 },
        { span: 24, major: 5, minor: 1 }
      ];
    }

    if (range > 1200) {
      return [
        { span: 3000, major: 500, minor: 100 },
        { span: 600, major: 100, minor: 25 },
        { span: 180, major: 25, minor: 5 },
        { span: 60, major: 10, minor: 2 },
        { span: 24, major: 5, minor: 1 }
      ];
    }

    return [
      { span: 600, major: 100, minor: 25 },
      { span: 180, major: 25, minor: 5 },
      { span: 60, major: 10, minor: 2 },
      { span: 24, major: 5, minor: 1 }
    ];
  }

  private clamp(year: number): number {
    return Math.max(this.minYear, Math.min(this.maxYear, year));
  }
}

function sensitivityForYear(year: number): number {
  // Lower = fewer years per pixel = less twitchy on small movements but
  // also less ground covered per drag. Deep BCE values are higher to keep
  // long-range scrubbing usable when the player is in "all" mode.
  if (year >= 1850) return 0.3;
  if (year >= 1500) return 0.4;
  if (year >= 1000) return 0.5;
  if (year >= 0) return 0.65;
  if (year >= -1000) return 1.0;
  if (year >= -10000) return 1.6;
  return 2.4;
}

function formatTimelineTick(year: number): string {
  const roundedYear = Math.round(year);

  if (roundedYear < 0) {
    const years = Math.abs(roundedYear);
    return years >= 10000 ? `${Math.round(years / 1000)}k BCE` : `${years} BCE`;
  }

  return String(roundedYear);
}
